import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components";
import { ZenCombobox } from "@/components/molecules/ComboBox";
import { ZenInput } from "@/components/molecules/Input";
import { useFormulario } from "./useFormulario";
import type { ZendeskItem, TicketField } from "@/services/zendesk";
import {
  zafClient,
  getTicketById,
  promoteToProblem,
  createTicket
} from "@/services/zendesk";
import { TICKET_CREATED_EVENT } from "@/pages/informacoes/useInformacoes";
import {
  ButtonContainer,
  FormularioContainer,
  ColumnsContainer,
  Column,
  LabelDescricao,
  TextareaCustom,
} from "./style";

import type { ZafMetadata } from '@/interfaces';

type CustomFieldValue = string | number | boolean;

interface CustomFieldPayloadItem {
  id: number | string;
  value: CustomFieldValue;
}

interface ZafApiError {
  responseText?: string;
  status?: number;
  message?: string;
}

export function Formulario() {
  const navigate = useNavigate();
  const form = useFormulario();
  const [dynamicOrderFieldId, setDynamicOrderFieldId] = useState<string | undefined>(undefined);

  // Carrega o ID do campo dinâmico das configurações do App
  useEffect(() => {
    async function loadMetadata() {
      if (zafClient) {
        const meta = await zafClient.metadata() as unknown as ZafMetadata;
        setDynamicOrderFieldId(meta.settings.orderIDField);
      }
    }
    loadMetadata();
  }, []);

  const handleCreateTicket = async () => {
    if (!zafClient) return;

    try {
      const currentId = form.ticketId;

      if (!currentId) {
        zafClient.invoke('notify', 'ERRO: ID do ticket pai não encontrado.', 'error');
        return;
      }

      const { ticket: parentData } = await getTicketById(currentId);

      if (parentData.type === "incident" && parentData.problem_id) {
        zafClient.invoke('notify', 'Este ticket já é um incidente vinculado.', 'error');
        return;
      }

      if (parentData.type !== "problem") {
        await promoteToProblem(currentId);
      }

      const brandId = form.raw.brands.find((i: ZendeskItem) => i.name === form.selectedBrand)?.id;
      const groupId = form.raw.groups.find((i: ZendeskItem) => i.name === form.selectedGroup)?.id;
      const formId = form.raw.forms.find((i: ZendeskItem) => i.name === form.selectedForm)?.id;
      const requesterId = form.raw.users.find((i: ZendeskItem) => i.name === form.selectedRequester)?.id;

      const customFieldsPayload: CustomFieldPayloadItem[] = Object.entries(form.customFields).map(([id, value]) => ({
        id: Number(id),
        value
      }));

      // Injeção do Order ID no campo dinâmico configurado
      if (form.orderId && dynamicOrderFieldId) {
        const fieldExistsInAccount = form.raw.fields.some(
          (f: TicketField) => String(f.id) === String(dynamicOrderFieldId)
        );

        if (fieldExistsInAccount) {
          customFieldsPayload.push({
            id: dynamicOrderFieldId,
            value: form.orderId
          });
        }
      }

      const childPayload: Record<string, unknown> = {
        subject: form.subject,
        comment: { body: form.description },
        type: 'incident',
        problem_id: currentId,
        brand_id: brandId,
        group_id: groupId,
        ticket_form_id: formId,
        requester_id: requesterId,
        custom_fields: customFieldsPayload
      };

      if (form.priority) {
        childPayload.priority = form.priority;
      }

      await createTicket(childPayload);

      window.dispatchEvent(new Event(TICKET_CREATED_EVENT));

      navigate('/modal/sucesso', { state: { ticketId: currentId } });

    } catch (error: unknown) {
      const apiError = error as ZafApiError;

      if (apiError?.responseText) {
        console.error('[ZAF] Detalhes da resposta:', apiError.responseText);
      }
      
      console.error('[ZAF] Erro completo:', error);
      zafClient.invoke('notify', 'Falha ao criar vínculo entre tickets.', 'error');
    }
  };

  const handleCustomFieldChange = (fieldId: number, value: CustomFieldValue) => {
    form.setCustomFields(prev => ({ ...prev, [fieldId]: value }));
  };

  return (
    <FormularioContainer>
      <ColumnsContainer>
        <Column>
          <ZenCombobox
            label="Marca"
            placeholder="Selecione uma marca"
            options={form.options.brands}
            value={form.selectedBrand}
            onChange={form.setSelectedBrand}
            disabled={form.loading}
          />
          <ZenCombobox
            label="Solicitante"
            placeholder="Selecione um solicitante"
            options={form.options.requesters}
            value={form.selectedRequester}
            onChange={form.setSelectedRequester}
            disabled={form.loading}
          />
          <ZenCombobox
            label="Grupo"
            placeholder="Selecione um grupo"
            options={form.options.groups}
            value={form.selectedGroup}
            onChange={form.setSelectedGroup}
            disabled={form.loading}
          />
          <ZenCombobox
            label="Formulário"
            placeholder="Selecione um formulário"
            options={form.options.forms}
            value={form.selectedForm}
            onChange={form.setSelectedForm}
            disabled={form.loading}
          />

          {form.activeFormFields.map((field: TicketField) => {
            if (['subject', 'description'].includes(field.type)) return null;
            // Oculta o campo de Order ID dinamicamente
            if (String(field.id) === String(dynamicOrderFieldId)) return null;

            switch (field.type) {
              case 'tagger':
              case 'multiselect':
                return (
                  <ZenCombobox
                    key={field.id}
                    label={field.title}
                    placeholder={`Selecione ${field.title.toLowerCase()}`}
                    options={field.custom_field_options?.map((opt) => opt.value) ?? []}
                    value={String(form.customFields[field.id] ?? '')}
                    onChange={(val) => handleCustomFieldChange(field.id, val)}
                    disabled={form.loading}
                  />
                );

              case 'tickettype':
                return (
                  <ZenCombobox
                    key={field.id}
                    label={field.title}
                    placeholder="Selecione o tipo"
                    options={field.system_field_options?.map((opt) => opt.value) ?? []}
                    value={String(form.customFields[field.id] ?? '')}
                    onChange={(val) => handleCustomFieldChange(field.id, val)}
                    disabled={form.loading}
                  />
                );

              case 'priority':
                return (
                  <ZenCombobox
                    key={field.id}
                    label={field.title}
                    placeholder="Selecione a prioridade"
                    options={field.system_field_options?.map((opt) => opt.value) ?? []}
                    value={form.priority}
                    onChange={(val) => form.setPriority(val)}
                    disabled={form.loading}
                  />
                );

              case 'text':
              case 'regexp':
              case 'integer':
              case 'decimal':
              case 'date':
                return (
                  <ZenInput
                    key={field.id}
                    label={field.title}
                    placeholder={`Preencha ${field.title.toLowerCase()}`}
                    value={String(form.customFields[field.id] ?? '')}
                    onChange={(val) => handleCustomFieldChange(field.id, val)}
                    type={field.type === 'date' ? 'date' : (['integer', 'decimal'].includes(field.type) ? 'number' : 'text')}
                    disabled={form.loading}
                  />
                );

              case 'textarea':
                return (
                  <div key={field.id} style={{ marginTop: '15px' }}>
                    <LabelDescricao>{field.title}</LabelDescricao>
                    <TextareaCustom
                      placeholder={`Preencha ${field.title.toLowerCase()}`}
                      value={String(form.customFields[field.id] ?? '')}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleCustomFieldChange(field.id, e.target.value)}
                      disabled={form.loading}
                    />
                  </div>
                );

              case 'checkbox':
                return (
                  <div key={field.id} style={{ display: 'flex', alignItems: 'center', marginTop: '15px', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form.customFields[field.id])}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCustomFieldChange(field.id, e.target.checked)}
                      disabled={form.loading}
                    />
                    <LabelDescricao style={{ margin: 0 }}>{field.title}</LabelDescricao>
                  </div>
                );

              default:
                return null;
            }
          })}
        </Column>

        <Column>
          <ZenInput
            label="Assunto"
            placeholder="Descreva o assunto"
            value={form.subject}
            onChange={(value: string) => form.setSubject(value)}
            disabled={form.loading}
          />
          <div style={{ marginTop: '15px' }}>
            <LabelDescricao>Descrição</LabelDescricao>
            <TextareaCustom
              placeholder="Detalhe sua solicitação"
              value={form.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => form.setDescription(e.target.value)}
              disabled={form.loading}
            />
          </div>
        </Column>
      </ColumnsContainer>

      <ButtonContainer style={{ marginTop: 'auto', justifyContent: 'flex-end', gap: '10px' }}>
        <Button isBasic onClick={() => navigate(-1)}>Voltar</Button>
        <Button
          isPrimary
          onClick={handleCreateTicket}
          disabled={form.loading || !form.subject || !form.description || !form.ticketId}
        >
          {form.loading ? "Carregando..." : "Criar ticket"}
        </Button>
      </ButtonContainer>
    </FormularioContainer>
  );
}